// SETTINGS V2 — phone bottom nav sheet (SETTINGS_V2_SP1 §7).
//
// Precedent — mechanics ported from customer/midas/components/v3/ConfirmDialogV3
// (portal to document.body + scope-class wrapper with transparent background +
// Escape + backdrop close + manual Tab focus trap + cached-activeElement focus
// restore). Visuals ported from designer SettingsPhoneShell sheet
// (settings-responsive.jsx lines 134-161): grouped 44px rows, rounded-top card,
// decorative handle bar. NO swipe/drag gestures — a nav sheet is tap-to-select;
// every close path is tap/keyboard (backdrop · Escape · row select · resize-out).
//
// Theme comes from Redux (settingsV2Ui.theme) — NO DOM theme sniff (unlike
// MIDAS, our theme is authoritative in the store). The portal wrapper carries
// `settings-v2-root p-{theme} mod-nastavenia` (NOT sv2-dotgrid) + inline
// background:transparent so the scoped CSS vars resolve INSIDE the portalled
// node (the transparent override defeats .settings-v2-root's opaque --bg paint).
//
// Styling stack: inline styles + scoped CSS-var tokens + NavLink. No MUI, no
// sx, no dt, no Tailwind. No new i18n keys. NO network/auth/localStorage work
// (SEC-REQ-2/3) — only Redux UI state + DOM focus.
import React, { useCallback, useEffect, useRef } from "react";
import ReactDOM from "react-dom";
import { NavLink, useLocation } from "react-router-dom";

import {
  useAppDispatch,
  useAppSelector,
} from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import useSettingsV2Nav, { isSettingsV2SectionActive } from "../useSettingsV2Nav";
import { SETTINGS_BASE } from "../settingsV2Paths";
import useHistoryBackClose from "../useHistoryBackClose";
import { setPhoneNavOpen } from "../settingsV2UiSlice";
import settingsV2NavGlyphs from "./settingsV2NavGlyphs";
import SettingsV2SheetIdentity from "./SettingsV2SheetIdentity";

// Active-row check icon — screen readers already get aria-current from NavLink,
// so this is decorative (aria-hidden). currentColor = the active row color.
const CheckMark: React.FC = () => (
  <svg
    viewBox="0 0 14 14"
    width={13}
    height={13}
    fill="none"
    stroke="currentColor"
    strokeWidth={1.6}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    style={{ flexShrink: 0 }}
  >
    <path d="M2.5 7.5 L5.5 10.5 L11.5 3.5" />
  </svg>
);

const SettingsV2PhoneNavSheet: React.FC = () => {
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { translate } = useTranslation("settings");
  const { groups } = useSettingsV2Nav();
  const open = useAppSelector((s) => s.settingsV2Ui.phoneNavOpen);
  const theme = useAppSelector((s) => s.settingsV2Ui.theme);

  const sheetRef = useRef<HTMLDivElement | null>(null);
  const activeRowRef = useRef<HTMLAnchorElement | null>(null);
  const cachedTriggerRef = useRef<HTMLElement | null>(null);

  const close = useCallback(() => {
    dispatch(setPhoneNavOpen(false));
  }, [dispatch]);

  // Hardware/gesture Back closes the sheet (SP1.1 / Q-SETV2-6 = B). Reusable
  // hook — future v2 phone sheets/dialogs share it. Rows navigate with
  // `replace` (below) per the hook's consumer contract so no stray entry lingers.
  useHistoryBackClose(open, close);

  // Focus: cache the trigger on open, move focus to the active row (fallback
  // first row) on next tick; restore focus to the trigger on close.
  useEffect(() => {
    if (open) {
      cachedTriggerRef.current =
        (document.activeElement as HTMLElement | null) || null;
      const id = window.setTimeout(() => {
        const target =
          activeRowRef.current ||
          sheetRef.current?.querySelector<HTMLElement>("a, button");
        target?.focus();
      }, 0);
      return () => window.clearTimeout(id);
    }
    const el = cachedTriggerRef.current;
    if (el && typeof el.focus === "function") el.focus();
    cachedTriggerRef.current = null;
    return undefined;
  }, [open]);

  // Keyboard: Escape → close; Tab/Shift-Tab cycle within the sheet only.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (!open) return;
      if (e.key === "Escape") {
        e.preventDefault();
        close();
        return;
      }
      if (e.key === "Tab") {
        const root = sheetRef.current;
        if (!root) return;
        const focusables = Array.from(
          root.querySelectorAll<HTMLElement>(
            'a, button, input, select, textarea, [tabindex]:not([tabindex="-1"])'
          )
        ).filter((el) => !el.hasAttribute("disabled") && el.tabIndex !== -1);
        if (focusables.length === 0) return;
        const first = focusables[0];
        const last = focusables[focusables.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    },
    [open, close]
  );

  useEffect(() => {
    if (!open) return undefined;
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [open, handleKeyDown]);

  if (!open || typeof document === "undefined") return null;

  const node = (
    <div
      className={`settings-v2-root p-${theme} mod-nastavenia`}
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 1500,
        background: "transparent",
      }}
      role="presentation"
      onMouseDown={(e) => {
        // Backdrop tap → close (only fires on the wrapper itself).
        if (e.target === e.currentTarget) close();
      }}
    >
      {/* Backdrop */}
      <div
        aria-hidden="true"
        style={{
          position: "absolute",
          inset: 0,
          background: "rgba(15, 23, 30, 0.32)",
          backdropFilter: "blur(2px)",
          WebkitBackdropFilter: "blur(2px)",
          animation: "sv2-fade-in 0.2s ease-out",
        }}
      />

      {/* Sheet panel */}
      <div
        ref={sheetRef}
        role="dialog"
        aria-modal="true"
        id="sv2-phone-nav-sheet"
        aria-label={translate("common:navigation.settings")}
        onMouseDown={(e) => e.stopPropagation()}
        style={{
          position: "absolute",
          left: 0,
          right: 0,
          bottom: 0,
          background: "var(--card)",
          borderRadius: "16px 16px 0 0",
          boxShadow: "0 -12px 40px rgba(15,23,30,0.18)",
          padding: "10px 10px 14px",
          maxHeight: "78vh",
          overflowY: "auto",
          overscrollBehavior: "contain",
          animation: "sv2-sheet-up 0.25s cubic-bezier(0.4,0,0.2,1)",
        }}
      >
        {/* Decorative handle bar (no drag). */}
        <div
          aria-hidden="true"
          style={{
            width: 36,
            height: 4,
            borderRadius: 999,
            background: "var(--border)",
            margin: "0 auto 8px",
          }}
        />

        {/* Identity row (SP1.1 / Q-SETV2-5 = B) — avatar + name, NO email. */}
        <SettingsV2SheetIdentity />

        {groups.map((group) => (
          <div key={group.key}>
            <div
              className="eyebrow"
              style={{ color: "var(--faint)", margin: "10px 8px 4px" }}
            >
              {translate(group.labelKey)}
            </div>
            {group.items.map((item) => {
              const active = isSettingsV2SectionActive(
                location.pathname,
                item.path
              );
              const Glyph = settingsV2NavGlyphs[item.key];
              return (
                <NavLink
                  key={item.key}
                  to={`${SETTINGS_BASE}/${item.path}`}
                  // `replace` consumes the Back-close sentinel in place
                  // (useHistoryBackClose consumer contract) — the destination
                  // overwrites the sentinel so no stray history entry lingers
                  // and one Back from the new section returns to the previous.
                  replace
                  ref={active ? activeRowRef : undefined}
                  className="sv2-sheet-row"
                  onClick={close}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 11,
                    height: 44,
                    padding: "0 10px",
                    borderRadius: 9,
                    minWidth: 0,
                    fontSize: 14,
                    textDecoration: "none",
                    // Inactive base color + hover/focus live in .sv2-sheet-row.
                    background: active ? "var(--mod-accent-soft)" : undefined,
                    color: active ? "var(--mod-accent)" : undefined,
                    fontWeight: active ? 600 : 500,
                  }}
                >
                  {Glyph && <Glyph />}
                  <span
                    style={{
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}
                  >
                    {translate(item.labelKey)}
                  </span>
                  <span style={{ flex: 1 }} />
                  {active && <CheckMark />}
                </NavLink>
              );
            })}
          </div>
        ))}
      </div>
    </div>
  );

  return ReactDOM.createPortal(node, document.body);
};

export default SettingsV2PhoneNavSheet;
